import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Wallet, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money, fdate } from "@/lib/api";
import { PageHeader, StatusBadge, Empty, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const Photo = ({ id }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    api.get(`/trainings/${id}/photo`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [id]);
  return url ? <img src={url} alt="Candidat" className="h-24 w-20 rounded-md object-cover" /> : <div className="h-24 w-20 rounded-md bg-slate-100" />;
};

const ApproveDialog = ({ t, onClose, onDone }) => {
  const [mods, setMods] = useState("");
  useEffect(() => { if (t) setMods(Array.from({ length: t.modules_count }, (_, i) => `Module ${i + 1} : `).join("\n")); }, [t]);
  const list = mods.split("\n").map((s) => s.trim()).filter(Boolean);
  const save = async () => {
    try { await api.post(`/trainings/${t.id}/approve`, { modules: list }); toast.success("Candidat approuvé — notification envoyée"); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!t} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="approve-dialog">
        <DialogHeader><DialogTitle>Approuver {t?.name}</DialogTitle></DialogHeader>
        <Field label="Modules constituant la formation (un par ligne)"><Textarea rows={5} value={mods} onChange={(e) => setMods(e.target.value)} data-testid="approve-modules-input" /></Field>
        <p className="text-sm text-slate-600">Montant : <b>{money(70 + 80 * list.length)}</b> ({list.length} module(s))</p>
        <DialogFooter><Button className="bg-emerald-600 hover:bg-emerald-700" onClick={save} disabled={!list.length} data-testid="approve-save-btn">Approuver et me notifier</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Trainings() {
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState(null);
  const load = () => api.get("/trainings").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const act = async (t, path, msg) => {
    try { const r = await api.post(`/trainings/${t.id}/${path}`); toast.success(path === "payment" && !r.data.program_sent ? "Paiement confirmé, mais l'email n'a pas pu être envoyé" : msg); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader kicker="Agent IA" title="Inscriptions aux formations" />
      {items.length ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {items.map((t) => (
            <div key={t.id} className="flex gap-4 rounded-md border border-slate-200 bg-white p-5" data-testid={`training-card-${t.number}`}>
              <Photo id={t.id} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div><span className="font-mono text-xs text-slate-500">{t.number} · {fdate(t.created_at)}</span><h3 className="font-display text-lg font-semibold">{t.name}</h3></div>
                  <StatusBadge status={t.status} testId={`training-status-${t.number}`} />
                </div>
                <p className="text-xs font-semibold uppercase tracking-wider text-red-600">{t.formation}</p>
                <div className="mt-2 space-y-1 text-xs text-slate-600">
                  <p className="flex items-center gap-1"><Phone className="h-3 w-3" /> {t.phone}</p>
                  <p className="flex items-center gap-1"><Mail className="h-3 w-3" /> {t.email}</p>
                  <p className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {t.address}</p>
                </div>
                {t.modules?.length > 0 && <ul className="mt-2 list-disc pl-4 text-xs text-slate-700">{t.modules.map((m) => <li key={m}>{m}</li>)}</ul>}
                <p className="mt-2 text-sm">Devis : <b>{money(t.quote.total)}</b></p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {t.status === "en_attente" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setCurrent(t)} data-testid={`training-approve-${t.number}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Approuver</Button>}
                  {t.status === "approuve" && <Button size="sm" className="bg-blue-900 hover:bg-blue-950" onClick={() => act(t, "payment", "Paiement confirmé — programme envoyé par email")} data-testid={`training-payment-${t.number}`}><Wallet className="mr-1 h-3.5 w-3.5" /> Confirmer le paiement</Button>}
                  {["en_attente", "approuve"].includes(t.status) && <Button size="sm" variant="outline" className="text-red-600" onClick={() => act(t, "reject", "Inscription refusée")} data-testid={`training-reject-${t.number}`}><XCircle className="mr-1 h-3.5 w-3.5" /> Refuser</Button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : <Empty testId="trainings-empty" text="Aucune inscription pour le moment." />}
      <ApproveDialog t={current} onClose={() => setCurrent(null)} onDone={() => { setCurrent(null); load(); }} />
    </div>
  );
}
